import styled from "@emotion/styled";
import { Tooltip } from "@mui/material";

import { FONT_WEIGHT_NORMAL, transientOptions } from "../../utilities";
import { useFormContext } from "../FormContext";
import { useOptionalFieldContext } from "../FormFieldContext";
import { Label } from "../Labels";

export const LabelDescription = styled("p", transientOptions)<{ $disabled?: boolean }>`
  font-weight: ${FONT_WEIGHT_NORMAL};
  margin: 6px 0;
  ${({ $disabled, theme }) => $disabled && `color: ${theme.generalTextIconVariant};`}
`;

const TooltipLabel = styled(Label, transientOptions)<{ $hasTooltip: boolean; $disabled?: boolean }>`
  ${({ $hasTooltip, theme }) =>
    $hasTooltip &&
    `
    &:hover {
      color: ${theme.primaryStrong};
    }
  `}

  ${({ $disabled, theme }) => $disabled && `color: ${theme.generalTextIconVariant};`}
`;

type FieldLabelProps = {
  description?: React.ReactNode;
  /** Show the description on hovering the label rather than in a block below it. */
  hideDescriptionInTooltip?: boolean;
} & Omit<React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>, "ref">;

export const FieldLabel = ({ description, hideDescriptionInTooltip, children, ...props }: FieldLabelProps) => {
  const context = useOptionalFieldContext();
  const { formatMessage, explicitLabels } = useFormContext();
  const explicitLabel = !formatMessage || explicitLabels || context?.explicitLabel;
  if (!explicitLabel) {
    console.error(
      `FieldLabel is being used in a form where explicitLabel/s is not set. Field Context: "${context?.name ?? ""}"`,
    );
  }
  const renderedChildren =
    children ?? (formatMessage && context?.name && explicitLabel ? formatMessage(`${context.name}Label`) : undefined);
  const renderedDescription =
    description ??
    (formatMessage && context?.name && explicitLabel ? formatMessage(`${context.name}Description`) : undefined);

  const showTooltip = !!hideDescriptionInTooltip && !!renderedDescription;

  return (
    <>
      <Tooltip
        title={showTooltip ? renderedDescription : undefined}
        placement="top"
        disableHoverListener={!showTooltip}
      >
        <TooltipLabel htmlFor={context?.controlId} $hasTooltip={showTooltip} $disabled={context?.disabled} {...props}>
          {renderedChildren}
        </TooltipLabel>
      </Tooltip>
      {renderedDescription && !hideDescriptionInTooltip && (
        <LabelDescription $disabled={context?.disabled}>{renderedDescription}</LabelDescription>
      )}
    </>
  );
};
